import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type T = { quote: string; name: string; role: string; logo?: string; avatar?: string };

const items: T[] = [
  {
    quote:
      "We finally know what our team really thinks. Anonymous feedback surfaced three retention risks we never would have caught in our annual survey.",
    name: "Mira Chen",
    role: "VP People, Northwind HR",
  },
  {
    quote:
      "SendFeedback became the backbone of our people strategy — over 3,000 honest signals captured with zero PII collected. It's not a survey tool, it's our culture engine.",
    name: "Devon Park",
    role: "Head of People Ops, Loop & Co.",
  },
  {
    quote:
      "Our managers used to fear surveys. Now they ask for them. The AI summaries make it impossible to ignore the patterns.",
    name: "Sasha Reyes",
    role: "Chief People Officer, Helix Group",
  },
  {
    quote:
      "The integration was instant and the UX doesn't make my hair fall out. Setup took an afternoon, insights started flowing the same week.",
    name: "Alex Baldovin",
    role: "Director of HR, Atlas Studio",
  },
];

export const TestimonialCarousel = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const scroll = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 5);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
              Customer Stories
            </h2>
            <p className="mt-3 text-muted-foreground">Real HR teams. Real outcomes.</p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => scroll(-1)}
              className="h-12 w-12 rounded-full border border-border bg-white inline-flex items-center justify-center hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              disabled={!canNext}
              onClick={() => scroll(1)}
              className="h-12 w-12 rounded-full bg-[hsl(var(--mkt-primary))] text-white inline-flex items-center justify-center hover:bg-[hsl(var(--mkt-primary-deep))] disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={ref}
          onScroll={onScroll}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
        >
          {items.map((t) => (
            <div
              key={t.name}
              className="snap-start shrink-0 w-[88%] sm:w-[480px] lg:w-[560px] bg-white border border-border rounded-3xl p-8 lg:p-10 mkt-shadow-card"
            >
              <p className="text-xl lg:text-2xl leading-snug text-foreground">"{t.quote}"</p>
              <div className="mt-8 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[hsl(var(--mkt-primary))] to-[hsl(var(--mkt-purple))] text-white inline-flex items-center justify-center font-bold">
                  {t.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <div className="font-bold text-foreground">{t.name}</div>
                  <div className="text-sm text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
